import { useEffect, useRef, useState } from 'react'
import { Capacitor } from '@capacitor/core'
import { FlightCard } from './FlightCard'
import { HomeContentCards } from './HomeContentCards'
import { ResizableDrawer } from './ui/resizable-drawer'
import {
  setBaseOverlayRect,
  clearBaseOverlayRect,
} from '@/lib/native/flight-map-overlay'
import { useTripGetMyNextFlight } from '../lib/hooks'
import { useCurrentCrewMember } from '../hooks/useCurrentCrewMember'

export function HomeFlightDrawer() {
  const { data: currentUser } = useCurrentCrewMember()
  const { data: nextFlight, isLoading: isNextFlightLoading } =
    useTripGetMyNextFlight(currentUser?.id)

  // Always show the flight card: it renders the upcoming flight, or — when there
  // is none — the local weather / "no location" state (FlightCard handles each
  // case internally, requesting device location itself).
  const showFlightCard = true

  // PassthroughWebView forwards touches over the fullscreen native map straight
  // to MKMapView. Register the drawer's rect so the webview captures touches
  // inside it instead — otherwise the drawer is undraggable.
  const [snap, setSnap] = useState<number | string>(0.3)
  const drawerHeight =
    typeof snap === 'number' ? `${snap * 100}vh` : snap
  // The flight card floats above the drawer, outside its interactive rect.
  // Union the card's height into the base rect so taps on it reach the webview
  // instead of passing through to the native map.
  const cardRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (Capacitor.getPlatform() !== 'ios') return
    if (typeof snap !== 'number') return
    const updateRect = () => {
      const vw = window.innerWidth
      const vh = window.innerHeight
      const drawerHeightPx = vh * snap
      const cardHeight = cardRef.current?.offsetHeight ?? 0
      const top = vh - drawerHeightPx - cardHeight
      setBaseOverlayRect({ x: 0, y: top, width: vw, height: vh - top })
    }
    updateRect()
    const el = cardRef.current
    if (!el) return
    const observer = new ResizeObserver(updateRect)
    observer.observe(el)
    return () => observer.disconnect()
  }, [snap, showFlightCard])
  useEffect(() => {
    if (Capacitor.getPlatform() !== 'ios') return
    return () => {
      clearBaseOverlayRect()
    }
  }, [])

  return (
    <>
      {showFlightCard && (
        <div
          ref={cardRef}
          className="fixed inset-x-0 z-50 content-frame px-gutter pb-4 transition-[bottom] duration-200 ease-out pointer-events-none"
          style={{ bottom: drawerHeight }}
        >
          <div className="pointer-events-auto max-w-[560px] mx-auto">
            <FlightCard
              nextFlight={nextFlight}
              isLoading={isNextFlightLoading || !currentUser}
              temperatureUnit={currentUser?.temperatureUnit}
              variant="glass"
            />
          </div>
        </div>
      )}
      <ResizableDrawer
        snapPoints={[0.3, 0.5, 0.7]}
        activeSnapPoint={snap}
        onActiveSnapPointChange={setSnap}
        handle={false}
        className="content-frame rounded-t-4xl bg-surface-brand-L1"
      >
        <div className="flex flex-col gap-6 px-gutter pt-4">
          <HomeContentCards />
          <div
            aria-hidden
            style={{ height: 'calc(72px + var(--safe-bottom))' }}
          />
        </div>
      </ResizableDrawer>
    </>
  )
}
